/**
 * ⌘K command palette — the power-tool credibility signal. Wired to real
 * navigation + quick actions, scoped to the current tenant. Opens on ⌘K / Ctrl-K.
 */
import { useEffect, useState } from "react";
import { Command } from "cmdk";
import { useNavigate } from "@tanstack/react-router";
import {
  LayoutDashboard,
  FileText,
  GitCompareArrows,
  CalendarClock,
  CalendarDays,
  ListChecks,
  FileSpreadsheet,
  ShieldCheck,
  ShieldAlert,
  Vote,
  Landmark,
  Users,
  ScrollText,
  Upload,
  FlaskConical,
  Grid3x3,
  Radar,
  BookText,
  ClipboardCheck,
  Search,
} from "lucide-react";

interface Item {
  label: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  keywords?: string;
}

export function CommandPalette({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    void navigate({ to, params: { slug } });
  };

  const nav: Item[] = [
    { label: "Dashboard", icon: LayoutDashboard, to: "/t/$slug/dashboard" },
    { label: "Conflict radar", icon: Radar, to: "/t/$slug/dashboard", keywords: "breach risk" },
    { label: "Documents", icon: FileText, to: "/t/$slug/documents" },
    {
      label: "Review queue",
      icon: ClipboardCheck,
      to: "/t/$slug/review-queue",
      keywords: "pending human review confirm edit reject hitl",
    },
    {
      label: "MFN triggers",
      icon: GitCompareArrows,
      to: "/t/$slug/mfn",
      keywords: "most favored nation",
    },
    {
      label: "What-if simulator",
      icon: FlaskConical,
      to: "/t/$slug/simulator",
      keywords: "simulate scenario grant",
    },
    {
      label: "LP comparison matrix",
      icon: Grid3x3,
      to: "/t/$slug/matrix",
      keywords: "grid compare",
    },
    {
      label: "Compliance calendar",
      icon: CalendarDays,
      to: "/t/$slug/calendar",
      keywords: "deadlines filings form adv pf overdue due",
    },
    {
      label: "Compliance requirements",
      icon: ListChecks,
      to: "/t/$slug/requirements",
      keywords: "catalog form adv pf code of ethics custody add",
    },
    {
      label: "Regulatory filings",
      icon: FileSpreadsheet,
      to: "/t/$slug/filings",
      keywords: "form pf adv draft filing-ready package prepare ai",
    },
    {
      label: "Side-letter obligations",
      icon: CalendarClock,
      to: "/t/$slug/obligations",
      keywords: "calendar deadlines lp reporting",
    },
    {
      label: "Compliance manual",
      icon: BookText,
      to: "/t/$slug/manual",
      keywords: "policies procedures 206(4)-7 manual annual review cco code of ethics tailored",
    },
    {
      label: "Form ADV Part 2A brochure",
      icon: FileText,
      to: "/t/$slug/brochure",
      keywords: "adv 2a brochure disclosure client item tailored",
    },
    {
      label: "Code of ethics",
      icon: ShieldCheck,
      to: "/t/$slug/ethics",
      keywords: "attestation acknowledgement holdings conduct",
    },
    {
      label: "Political contributions",
      icon: Vote,
      to: "/t/$slug/contributions",
      keywords: "pay to play 206(4)-5 de minimis lookback",
    },
    {
      label: "Restricted list",
      icon: ShieldAlert,
      to: "/t/$slug/restricted-list",
      keywords: "mnpi wall crossing watch list 204a information barrier insider screen",
    },
    { label: "Funds", icon: Landmark, to: "/t/$slug/funds" },
    { label: "Limited partners", icon: Users, to: "/t/$slug/lps", keywords: "lp investors" },
    { label: "Audit trail", icon: ScrollText, to: "/t/$slug/audit", keywords: "log evidence" },
  ];
  const actions: Item[] = [
    { label: "Upload a document", icon: Upload, to: "/t/$slug/documents/upload" },
  ];

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command menu"
      className="fixed inset-0 z-50 flex items-start justify-center"
    >
      <button
        type="button"
        aria-label="Close command menu"
        tabIndex={-1}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <div className="relative z-10 mt-[12vh] w-full max-w-xl overflow-hidden rounded-xl border border-neutral-800 bg-neutral-950/95 shadow-2xl shadow-black/60">
        <div className="flex items-center gap-2 border-b border-neutral-800 px-3.5">
          <Search className="size-4 text-neutral-500" />
          <Command.Input
            placeholder="Search Argus — go to anything, run an action…"
            className="h-12 w-full bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-500"
          />
        </div>
        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          <Command.Empty className="px-3 py-6 text-center text-sm text-neutral-500">
            No matches.
          </Command.Empty>
          <Command.Group
            heading="Go to"
            className="px-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-neutral-600"
          >
            {nav.map((item) => (
              <PaletteItem key={item.label} item={item} onSelect={() => go(item.to)} />
            ))}
          </Command.Group>
          <Command.Group
            heading="Actions"
            className="px-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-neutral-600"
          >
            {actions.map((item) => (
              <PaletteItem key={item.label} item={item} onSelect={() => go(item.to)} />
            ))}
          </Command.Group>
        </Command.List>
      </div>
    </Command.Dialog>
  );
}

function PaletteItem({ item, onSelect }: { item: Item; onSelect: () => void }) {
  const Icon = item.icon;
  return (
    <Command.Item
      value={`${item.label} ${item.keywords ?? ""}`}
      onSelect={onSelect}
      className="aria-selected:bg-brand-950/60 flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-sm text-neutral-300 aria-selected:text-neutral-50"
    >
      <Icon className="size-4 text-neutral-500" />
      {item.label}
    </Command.Item>
  );
}
